/**
 * Analytics Utility
 *
 * Centralized analytics tracking for GTM/GA4 events.
 * All tracking functions are safe to call even if GTM is not loaded.
 */

// Extend window to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize dataLayer if it doesn't exist
if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = [];
}

/**
 * Push event to GTM dataLayer
 */
export const pushToDataLayer = (event: Record<string, any>): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
  }
};

/**
 * Track a generic event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    ...eventParams,
  });
};

// ============================================================================
// CTA Tracking
// ============================================================================

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (
  ctaName: string,
  ctaLocation: string,
  destination?: string
): void => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination: destination,
  });
};

/**
 * Common CTA tracking functions
 */
export const trackTrialStart = (location: string): void => {
  trackCTAClick('trial_start', location, '/signup');
};

export const trackDemoRequest = (location: string): void => {
  trackCTAClick('demo_request', location);
};

export const trackPricingView = (location: string): void => {
  trackCTAClick('pricing_view', location, '/planos');
};

// ============================================================================
// Lead Tracking
// ============================================================================

/**
 * Track lead form submissions
 */
export const trackLeadSubmission = (
  formType: 'newsletter' | 'contact' | 'lead_magnet' | 'exit_intent',
  success: boolean,
  additionalData?: Record<string, any>
): void => {
  trackEvent('lead_submission', {
    form_type: formType,
    success: success,
    ...additionalData,
  });
};

/**
 * Track newsletter signup
 */
export const trackNewsletterSignup = (success: boolean): void => {
  trackLeadSubmission('newsletter', success);
};

/**
 * Track contact form submission
 */
export const trackContactFormSubmission = (
  success: boolean,
  subject?: string
): void => {
  trackLeadSubmission('contact', success, { subject });
};

/**
 * Track lead magnet download
 */
export const trackLeadMagnetDownload = (
  magnetName: string,
  success: boolean
): void => {
  trackLeadSubmission('lead_magnet', success, { magnet_name: magnetName });
};

/**
 * Track exit intent popup interaction
 */
export const trackExitIntentPopup = (
  action: 'shown' | 'dismissed' | 'converted',
  email?: string
): void => {
  trackEvent('exit_intent', {
    action: action,
    has_email: !!email,
  });
};

// ============================================================================
// Page & User Tracking
// ============================================================================

/**
 * Track page view (for SPA navigation)
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track user signup
 */
export const trackSignup = (
  planId: string,
  source?: string
): void => {
  trackEvent('sign_up', {
    plan_id: planId,
    signup_source: source,
  });
};

/**
 * Track user login
 */
export const trackLogin = (method: string = 'email'): void => {
  trackEvent('login', {
    method: method,
  });
};

// ============================================================================
// Conversion Tracking
// ============================================================================

/**
 * Track subscription purchase
 */
export const trackPurchase = (
  planId: string,
  value: number,
  paymentMethod: 'pix' | 'credit_card'
): void => {
  trackEvent('purchase', {
    plan_id: planId,
    value: value,
    currency: 'BRL',
    payment_method: paymentMethod,
  });
};

/**
 * Track checkout started
 */
export const trackCheckoutStart = (planId: string, value: number): void => {
  trackEvent('begin_checkout', {
    plan_id: planId,
    value: value,
    currency: 'BRL',
  });
};

// ============================================================================
// Engagement Tracking
// ============================================================================

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number): void => {
  trackEvent('scroll_depth', {
    percent_scrolled: percentage,
  });
};

/**
 * Track time on page (call on unmount)
 */
export const trackTimeOnPage = (seconds: number, pagePath: string): void => {
  trackEvent('time_on_page', {
    time_seconds: seconds,
    page_path: pagePath,
  });
};

/**
 * Track feature interaction
 */
export const trackFeatureClick = (featureName: string): void => {
  trackEvent('feature_click', {
    feature_name: featureName,
  });
};

// ============================================================================
// UTM Parameter Handling
// ============================================================================

/**
 * Get UTM parameters from URL
 */
export const getUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  // Also get referrer
  if (document.referrer) {
    utmParams.referrer = document.referrer;
  }

  return utmParams;
};

/**
 * Store UTM parameters in sessionStorage for later use
 */
export const storeUTMParams = (): void => {
  const utmParams = getUTMParams();
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
};

/**
 * Retrieve stored UTM parameters
 */
export const getStoredUTMParams = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Build a signup URL preserving any UTM params captured at landing.
 * Use in CTAs that route to /signup so attribution survives the click.
 */
export const buildSignupUrl = (
  basePath: string = '/signup',
  extraParams?: Record<string, string>
): string => {
  const utms = getStoredUTMParams();
  const merged: Record<string, string> = { ...utms, ...extraParams };
  const params = new URLSearchParams();
  Object.entries(merged).forEach(([k, v]) => {
    if (v) params.append(k, v);
  });
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

// Auto-store UTM params on load
if (typeof window !== 'undefined') {
  storeUTMParams();
}

export default {
  trackEvent,
  trackCTAClick,
  trackTrialStart,
  trackDemoRequest,
  trackPricingView,
  trackLeadSubmission,
  trackNewsletterSignup,
  trackContactFormSubmission,
  trackLeadMagnetDownload,
  trackExitIntentPopup,
  trackPageView,
  trackSignup,
  trackLogin,
  trackPurchase,
  trackCheckoutStart,
  trackScrollDepth,
  trackTimeOnPage,
  trackFeatureClick,
  getUTMParams,
  getStoredUTMParams,
  buildSignupUrl,
};
